import { MODULES, type ModuleEntry } from './modules.tsx'
import type { WorkspaceModule } from './workspace.ts'

/**
 * Quais telas são abas de qual página de configuração (F068).
 *
 * Separado de `components/config-tabs.tsx` porque o Fast Refresh não atualiza
 * um módulo que mistura componentes com outras exportações — a mesma razão que
 * separou `lib/ticket-meta.tsx` de `components/ticket-badges.tsx` na F061.
 *
 * Não há uma segunda lista aqui: a fonte é `MODULES`, e a ordem das abas é a
 * ordem em que `MODULES` declara as telas. Uma lista à parte seria uma lista
 * que envelhece — a tela nova entraria no menu e não na trilha, ou o contrário,
 * e as duas falham em silêncio.
 */

/** As abas de uma página de configuração, na ordem em que `MODULES` as declara. */
export const tabsOf = (hub: WorkspaceModule): readonly ModuleEntry[] =>
  MODULES.filter((entry) => entry.inTab === hub)

/**
 * O rótulo curto, quando a tela declara um.
 *
 * "Canais de atendimento" é o nome certo numa lista onde ele convive com
 * "Canais" (marketplaces); dentro de uma página chamada *Configurações do
 * atendimento* ele vira redundante e come a largura da trilha.
 */
export const tabLabelOf = (entry: ModuleEntry): string =>
  entry.tabLabel ?? entry.label

/**
 * Em que aba a página de configuração abre, para QUEM está olhando.
 *
 * A aba marcada `isTabDefault` quando a pessoa pode abri-la; senão a primeira
 * que ela pode. As duas páginas de configuração chamam esta função em vez de
 * cada uma repetir o `find` — eram duas cópias idênticas, e foi por isso que a
 * porta de entrada de `/configuracoes` mudou sozinha quando a F088 declarou uma
 * aba nova no começo de `MODULES`.
 *
 * `canOpen` vem de fora porque a permissão mora no `useAuth`, e uma função que
 * lê contexto de React deixaria de ser testável sem montar um provedor.
 */
export const defaultTabOf = (
  hub: WorkspaceModule,
  canOpen: (entry: ModuleEntry) => boolean
): ModuleEntry | undefined => {
  const visible = tabsOf(hub).filter(canOpen)
  return visible.find((entry) => entry.isTabDefault === true) ?? visible[0]
}
